import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { pagesData } from '../data/pagesContent';
import { pagesMarkdownDA } from '../data/pagesMarkdownDA';
import { pagesMarkdownEN } from '../data/pagesMarkdownEN';
import { parseMarkdownBlocks, renderInline } from '../lib/renderMarkdown';

export default function ContentPage({ slug }) {
  const page = pagesData[slug];
  if (!page) {
    return (
      <div className="py-24 text-center">
        <h1 className="text-2xl font-bold">Side ikke fundet</h1>
        <Link to="/" className="mt-4 inline-block text-red-600 underline">Gå til forsiden</Link>
      </div>
    );
  }

  const isEn = page.lang === 'en';
  const markdown = isEn ? pagesMarkdownEN[slug] : pagesMarkdownDA[slug];
  const { title, lead, blocks } = parseMarkdownBlocks(markdown || '');

  // Group projects into simple cards if on referencer / en-references page
  const processedBlocks = React.useMemo(() => {
    if (slug !== 'referencer' && slug !== 'en-references') {
      return blocks;
    }

    const result = [];
    let i = 0;
    while (i < blocks.length) {
      const block = blocks[i];
      if (block.type === 'h2' && (block.text.includes('Udvalgte projekter') || block.text.includes('Selected projects'))) {
        result.push(block);
        i++;

        // Collect following h3 + p blocks into projects
        const projects = [];
        while (i < blocks.length && blocks[i].type !== 'h2') {
          if (blocks[i].type === 'h3') {
            const projectTitle = blocks[i].text;
            let category = '';
            let description = '';
            i++;

            // Check if next is category
            if (i < blocks.length && blocks[i].type === 'p' && blocks[i].text.startsWith('**')) {
              category = blocks[i].text.replace(/\*\*/g, '').trim();
              i++;
            }

            // Check if next is description
            if (i < blocks.length && blocks[i].type === 'p') {
              description = blocks[i].text;
              i++;
            }

            projects.push({ title: projectTitle, category, description });
          } else {
            result.push(blocks[i]);
            i++;
          }
        }

        if (projects.length > 0) {
          result.push({ type: 'projects_grid', projects });
        }
      } else {
        result.push(block);
        i++;
      }
    }
    return result;
  }, [blocks, slug]);

  return (
    <>
      <Helmet>
        <html lang={page.lang} />
        <title>{page.title}</title>
        <meta name="description" content={page.meta.description} />
        <meta property="og:title" content={page.meta.ogTitle} />
        <meta property="og:description" content={page.meta.ogDescription} />
        <meta property="og:image" content="https://assets.auxodev.dk/assets/7f5366e1-5aaf-452f-9c72-7b11437bc331/hero.jpg" />
        <link rel="canonical" href={`https://billundvvs.netlify.app${page.route === '/' ? '/' : page.route + '/'}`} />
      </Helmet>

      {/* Page Header (Bright Nordic daylight style) */}
      <section className="relative overflow-hidden py-14 sm:py-20 border-b border-slate-200">
        {/* Background image */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/assets/7f5366e1-5aaf-452f-9c72-7b11437bc331/image.png"
            alt="Billund Varme & Sanitet"
            className="w-full h-full object-cover object-center"
          />
          {/* Subtle light overlay to ensure clean readability while keeping the image visible */}
          <div className="absolute inset-0 bg-white/75 backdrop-blur-[1px]" />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-left">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-slate-900 leading-tight drop-shadow-[0_1px_2px_rgba(255,255,255,0.8)]">
            {renderInline(title || page.title, 'header-title')}
          </h1>

          {lead && (
            <p className="mt-4 text-base sm:text-lg text-slate-800 font-medium leading-relaxed max-w-3xl drop-shadow-[0_1px_1px_rgba(255,255,255,0.8)]">
              {renderInline(lead, 'header-lead')}
            </p>
          )}

          {/* Action CTAs */}
          <div className="mt-8 flex flex-wrap items-center justify-start gap-4">
            <Link
              to={isEn ? '/en/get-a-quote' : '/tilbud'}
              className="bg-[#d3102e] hover:bg-[#b7181d] text-white font-bold text-xs uppercase tracking-wider px-6 py-3.5 rounded-full inline-flex items-center gap-2 transition-all shadow-sm hover:shadow"
            >
              <span>{isEn ? 'GET A FREE QUOTE' : 'FÅ ET UFORPLIGTENDE TILBUD'}</span>
              <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true"></i>
            </Link>

            <a
              href="tel:+4575331276"
              className="bg-white/95 hover:bg-white border border-slate-300 text-slate-800 font-bold text-xs uppercase tracking-wider px-6 py-3.5 rounded-full transition-all inline-flex items-center gap-2 shadow-xs backdrop-blur-xs"
            >
              <i className="fa-solid fa-phone text-[#d3102e]" aria-hidden="true"></i>
              <span>{isEn ? 'CALL 75 33 12 76' : 'RING 75 33 12 76'}</span>
            </a>
          </div>
        </div>
      </section>

      {/* Main Content Body */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {processedBlocks.map((block, idx) => {
            if (block.type === 'projects_grid') {
              return (
                <div key={idx} className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6 my-8">
                  {block.projects.map((proj, pIdx) => (
                    <div
                      key={pIdx}
                      className="bg-white rounded-xl border border-slate-200/90 p-5 sm:p-6 shadow-xs hover:border-slate-300 hover:shadow-sm transition-all flex flex-col justify-between"
                    >
                      <div>
                        {proj.category && (
                          <div className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#d3102e] bg-red-50/90 border border-red-100/70 px-2.5 py-1 rounded-md mb-3">
                            <i className="fa-solid fa-tag text-[10px] text-[#d3102e]" aria-hidden="true" />
                            <span>{proj.category}</span>
                          </div>
                        )}
                        <h3 className="text-base sm:text-lg font-bold text-slate-900 mb-2 leading-snug">
                          {proj.title}
                        </h3>
                        <p className="text-slate-600 text-sm leading-relaxed">
                          {proj.description}
                        </p>
                      </div>
                      <div className="mt-5 pt-3.5 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500 font-medium">
                        <span className="inline-flex items-center gap-1.5 text-slate-500">
                          <i className="fa-solid fa-circle-check text-emerald-600 text-[11px]" aria-hidden="true" />
                          <span>{isEn ? 'Completed assignment' : 'Afsluttet opgave'}</span>
                        </span>
                        <span className="text-[#d3102e] font-semibold text-[11px] tracking-wide uppercase">
                          Billund VVS
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              );
            }

            if (block.type === 'h2') {
              return (
                <h2
                  key={idx}
                  className="text-2xl sm:text-3xl font-extrabold text-slate-900 mt-12 mb-5 first:mt-0 tracking-tight"
                >
                  {renderInline(block.text, `h2-${idx}`)}
                </h2>
              );
            }

            if (block.type === 'h3') {
              return (
                <h3
                  key={idx}
                  className="text-lg sm:text-xl font-bold text-slate-900 mt-8 mb-3"
                >
                  {renderInline(block.text, `h3-${idx}`)}
                </h3>
              );
            }

            if (block.type === 'p') {
              return (
                <p
                  key={idx}
                  className="text-slate-600 text-sm sm:text-base leading-relaxed mb-5"
                >
                  {renderInline(block.text, `p-${idx}`)}
                </p>
              );
            }

            if (block.type === 'image') {
              let imgSrc = block.src;
              if (imgSrc.includes('nybyg.jpg')) {
                imgSrc = '/cdn-cgi/image/width=1000,quality=80,format=auto/_assets/nybyg.jpg';
              }
              return (
                <div key={idx} className="my-8 rounded-xl overflow-hidden border border-slate-200 shadow-xs">
                  <img
                    src={imgSrc}
                    alt={block.alt}
                    className="w-full h-auto max-h-[460px] object-cover"
                  />
                  {block.alt && (
                    <div className="bg-slate-50 px-4 py-2 text-xs text-slate-500 border-t border-slate-100">
                      {block.alt}
                    </div>
                  )}
                </div>
              );
            }

            if (block.type === 'table') {
              return (
                <div key={idx} className="my-8 overflow-x-auto border border-slate-200 rounded-xl shadow-xs">
                  <table className="min-w-full divide-y divide-slate-200 text-xs sm:text-sm text-left">
                    <thead className="bg-slate-100">
                      <tr>
                        {block.headers.map((h, hIdx) => (
                          <th
                            key={hIdx}
                            className="px-4 sm:px-6 py-3.5 font-bold uppercase tracking-wider text-slate-800 text-[11px] sm:text-xs"
                          >
                            {renderInline(h, `th-${idx}-${hIdx}`)}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-slate-100">
                      {block.rows.map((row, rIdx) => (
                        <tr key={rIdx} className={rIdx % 2 === 0 ? 'bg-white' : 'bg-slate-50/60'}>
                          {row.map((cell, cIdx) => (
                            <td
                              key={cIdx}
                              className={`px-4 sm:px-6 py-3.5 text-slate-700 leading-relaxed ${
                                cIdx === 0 ? 'font-semibold text-slate-900' : ''
                              }`}
                            >
                              {renderInline(cell, `td-${idx}-${rIdx}-${cIdx}`)}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            }

            if (block.type === 'ul') {
              return (
                <ul key={idx} className="my-6 space-y-2.5 text-sm sm:text-base text-slate-700">
                  {block.items.map((item, iIdx) => (
                    <li key={iIdx} className="flex items-start gap-3">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#d3102e] mt-2 shrink-0"></span>
                      <span>{renderInline(item, `ul-${idx}-${iIdx}`)}</span>
                    </li>
                  ))}
                </ul>
              );
            }

            if (block.type === 'ol') {
              return (
                <div key={idx} className="my-8 space-y-4">
                  {block.items.map((item, iIdx) => (
                    <div key={iIdx} className="flex items-start gap-4">
                      <div className="w-7 h-7 rounded-full bg-[#d3102e] text-white flex items-center justify-center font-bold text-xs shrink-0 mt-0.5">
                        {item.num}
                      </div>
                      <div className="text-sm sm:text-base text-slate-700 leading-relaxed">
                        {renderInline(item.text, `ol-${idx}-${iIdx}`)}
                      </div>
                    </div>
                  ))}
                </div>
              );
            }

            if (block.type === 'faq_q') {
              return (
                <div key={idx} className="mt-8 mb-2 pt-4 border-t border-slate-200">
                  <h4 className="font-bold text-base sm:text-lg text-slate-900 flex items-center gap-2">
                    <span className="text-[#d3102e]">Q:</span>
                    <span>{renderInline(block.text, `faq-${idx}`)}</span>
                  </h4>
                </div>
              );
            }

            return null;
          })}
        </div>
      </section>
    </>
  );
}
